import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';

import { map, switchMap, tap } from 'rxjs/operators';

import { AuditLogsActions } from 'src/app/features/audit-logs';
import { CheckInActions } from 'src/app/features/check-in/store';
import { NextStep } from 'src/app/features/check-in/models';
import { DeviceGroupActions, DeviceGroupSelectors } from 'src/app/features/device-group';
import { MultiFactorActions } from 'src/app/features/multi-factor/store/actions';
import { PackageUsersActions, PackageUsersSelectors } from 'src/app/features/package-users';
import { PackagesSelectors } from 'src/app/features/packages';
import { PinValidationActions } from 'src/app/features/pin-validation';
import { AudioVideoCheckSelectors } from 'src/app/features/av-check/store/selectors';
import { SignalRActions } from 'src/app/features/signal-r';

import { SessionHandoffActions } from '../actions';
import { getAssociatedUsers } from '../selectors/session-handoff.selectors';

@Injectable()
export class SessionHandoffEffects {
  activateSessionHandoff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionHandoffActions.ActivateSessionHandoff),
      concatLatestFrom(() => [
        this.store.select(DeviceGroupSelectors.getUsersOnDevice),
        this.store.select(getAssociatedUsers),
      ]),
      switchMap(([_, usersOnDevice, associatedUsers]) => {
        const usersOnDeviceButNotAssociated = usersOnDevice.filter(
          (deviceUser) =>
            !associatedUsers.some(
              (userGuid) => userGuid.toLowerCase() === deviceUser.packageUserGuid.toLowerCase()
            )
        );
        return usersOnDeviceButNotAssociated.map((deviceUser) =>
          SessionHandoffActions.AddAssociatedUser({
            payload: { packageUserGuid: deviceUser.packageUserGuid },
          })
        );
      })
    )
  );

  joinSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionHandoffActions.JoinSession),
      concatLatestFrom((action) => [
        this.store.select(PackageUsersSelectors.getPackageUser(action.payload.packageUserGuid)),
      ]),
      switchMap(([action, participant]) => {
        if (!participant || participant.isMissingVerificationType) {
          return [];
        }

        if (participant.isMfaRequired) {
          return [
            MultiFactorActions.StartMultiFactorChallengeFromModalComponent({
              payload: {
                packageUserGuid: participant.packageUserGuid,
                onSuccessAction: SessionHandoffActions.JoinSessionSuccess({
                  payload: action.payload,
                }),
              },
            }),
          ];
        }

        return [
          PinValidationActions.StartPinChallenge({
            payload: {
              participant: participant,
              onSuccessAction: SessionHandoffActions.JoinSessionSuccess({
                payload: action.payload,
              }),
            },
          }),
        ];
      })
    )
  );

  joinSessionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionHandoffActions.JoinSessionSuccess),
      switchMap(({ payload }) => [
        SessionHandoffActions.AddAssociatedUser({ payload }),
        SignalRActions.AttemptSignalRHubConnection({ payload }),
        AuditLogsActions.SaveReadyUpEvent({ payload }),
        CheckInActions.ActiveSessionCheckInCompletion({ payload }),
        CheckInActions.SetUserCheckInComplete({ payload }),
        DeviceGroupActions.SaveToDeviceGroup({ payload }),
        PackageUsersActions.SetActivePackageUserGuid({
          payload: { activePackageUserGuid: payload.packageUserGuid },
        }),
      ])
    )
  );

  continueToSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionHandoffActions.ContinueToSession),
        concatLatestFrom(() => [
          this.store.pipe(
            select(AudioVideoCheckSelectors.getDoesAvCheckNeedToBeCompletedInSession)
          ),
          this.store.pipe(select(PackagesSelectors.getProductRoute)),
        ]),
        tap(([_, needsAvCheck, productRoute]) => {
          if (needsAvCheck) {
            this.router.navigate([`/${productRoute}/av-check`], {
              queryParamsHandling: 'preserve',
            });
          } else {
            this.router.navigate([`/${productRoute}/waiting-room`], {
              queryParamsHandling: 'preserve',
            });
          }
        }),
        map(() => CheckInActions.SetNextStep({ payload: { nextStep: NextStep.WaitingRoom } }))
      )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly router: Router,
  ) {}
}
